import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import AxisNumberInput from "../ui/AxisNumberInput";
import FieldLabel from "../ui/FieldLabel";
import Separator from "../ui/Separator";
import {
  useBodies,
  useJoints,
  useActuators,
  useSceneActions,
  useSceneStore,
  useWorldBodies,
  useEqualities,
  useTendons,
} from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { ArrowLeft, ArrowRight, ArrowUp, Move3d, RotateCcw } from "lucide-react";
import { findBodyForJointUuid } from "../../utils/bodyHierarchy";
import { resolveJointUuidFromRef } from "../../utils/jointUtils";
import { getWorldBodyLabelForBodyUuid } from "../../utils/worldBodyLabels";
import { useNavigateToObjectFromBodyUuid } from "../../hooks/useNavigateToObjectFromBodyUuid";
import { JointVisibilityToolbar } from "../../selected-joint-visibility";
import { useInspectorMobileScenePeek } from "./inspectorMobileScenePeekContext";
import { useTransformGizmoControls } from "./useTransformGizmoControls";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../../utils/inspectorChromeButtonClasses";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { EntireObjectIcon } from "../ui/Icons";
import RelatedConstraintsSection, {
  computeRelatedConstraintsLists,
} from "./RelatedConstraintsSection";
import Tooltip from "../ui/Tooltip";
import MenuSelect from "../ui/MenuSelect";
import ActuatorAccordion from "./ActuatorAccordion";
import InfoTooltip from "../ui/InfoTooltip";
import { jointStructuralEqualIgnoreRefAndRange } from "../../utils/jointStructuralEqualIgnoreRef";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";
import { getJointTypeMenuOptions } from "../../utils/jointTypeValidation";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";
import { logger } from "../../utils/logger";
import BallJointActuatorsSection from "./BallJointActuatorsSection";
import JointAxisEditor from "./JointAxisEditor";
import JointPhysicsSection from "./JointPhysicsSection";
import JointScalarTripleRangeSlider from "./JointScalarTripleRangeSlider";
import SlideJointPositionOffsetInput from "./SlideJointPositionOffsetInput";

const isValidJointAxis = (axis) => {
  if (!Array.isArray(axis) || axis.length < 3) return false;
  const values = axis.slice(0, 3).map(Number);
  return values.every(Number.isFinite) && Math.hypot(...values) >= 1e-8;
};

const SNAP_AXIS_EPSILON = 1e-3;

const JointPropertiesPanel = ({
  quickNavBackTarget = null,
  onQuickNavBack = null,
  quickNavForwardTarget = null,
  onQuickNavForward = null,
}) => {
  const selectedJoint = useSelectionStore((state) => state.selectedJoint);
  const selectJointRecord = useCallback(
    (jm) => (selectedJoint ? (jm?.[selectedJoint] ?? null) : null),
    [selectedJoint]
  );
  const joint = useJoints(selectJointRecord, jointStructuralEqualIgnoreRefAndRange);
  const jointMap = useJoints();
  const tendons = useTendons();
  const equalities = useEqualities();
  const actuators = useActuators();
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const robotActions = useSceneActions();
  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const disableJointEditing = authoringLocked;
  const { showPositionGizmo, togglePositionGizmo } = useTransformGizmoControls({
    disabled: transformEditorBlock.disabled,
    enableShortcuts: true,
  });
  const selectionActions = useSelectionStore.getState();
  const inspectorMobileScenePeek = useInspectorMobileScenePeek();

  // Local state for input values while editing (to prevent cursor jumping)
  const [editingInputs, setEditingInputs] = useState({});
  const [axisValidationMessage, setAxisValidationMessage] = useState("");

  useEffect(() => {
    setEditingInputs({});
    setAxisValidationMessage("");
  }, [selectedJoint]);

  const jointMapEntry = joint;
  const axisForInfo = jointMapEntry?.axis ?? joint?.axis ?? [0, 0, 1];
  const posForInfo = Array.isArray(joint?.position) ? joint.position : [0, 0, 0];

  const jointBodyUuid = useBodies((bodies) =>
    joint?.uuid ? findBodyForJointUuid(bodies, joint.uuid) : null
  );
  const objectLabel = useBodies((bodies) =>
    getWorldBodyLabelForBodyUuid(jointBodyUuid, bodies, worldBodies)
  );
  const jointBodyName = jointBodyUuid
    ? bodies?.[jointBodyUuid]?.name ||
      Object.values(bodies || {}).find((body) => body?.uuid === jointBodyUuid)?.name
    : null;
  const { onNavigateToObject: handleNavigateToObjectProperties } =
    useNavigateToObjectFromBodyUuid(jointBodyUuid);

  const canGoBack = !!quickNavBackTarget && typeof onQuickNavBack === "function";
  const canGoForward = !!quickNavForwardTarget && typeof onQuickNavForward === "function";
  const jointActuators = useMemo(() => {
    if (!joint?.uuid) return [];
    const jUuid = joint.uuid;
    const jointsMap = useSceneStore.getState().joints ?? {};
    return Object.values(actuators || {}).filter((a) => {
      if (!a?.joint || typeof a.joint !== "string") return false;
      if (a.joint === jUuid) return true;
      return resolveJointUuidFromRef(a.joint, jointsMap) === jUuid;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `joint?.name` matters for `resolveJointUuidFromRef` when actuators reference the joint by display name (uuid alone misses renames).
  }, [actuators, joint?.uuid, joint?.name]);
  const hasRelatedConstraints = useMemo(() => {
    if (!joint?.uuid) return false;
    const { relatedEqualities, relatedTendons } = computeRelatedConstraintsLists({
      jointUuid: joint.uuid,
      bodies,
      tendons,
      equalities,
    });
    return relatedEqualities.length > 0 || relatedTendons.length > 0;
  }, [joint?.uuid, bodies, tendons, equalities]);

  const jointTypeOptions = useMemo(() => {
    return getJointTypeMenuOptions({
      bodies,
      joints: jointMap,
      bodyUuid: jointBodyUuid,
      excludeJointUuid: joint?.uuid,
      currentKind: joint?.kind ?? "hinge",
      includeFree: false,
    }).map(({ kind, ...option }) => ({ ...option, value: kind }));
  }, [bodies, joint?.kind, joint?.uuid, jointBodyUuid, jointMap]);

  if (!joint || joint.kind === "free") return null;

  const hasScalarValueRange = joint.kind === "hinge" || joint.kind === "slide";
  const hasPivotPosition = joint.kind === "hinge" || joint.kind === "ball";
  const hasRotationAxis = hasScalarValueRange;
  const canAddJointActuator = hasScalarValueRange;
  const showScalarActuatorsSection = hasScalarValueRange;
  const showBallActuatorsSection = joint.kind === "ball";

  const backLabel = canGoBack ? `Back (ALT + ←): ${quickNavBackTarget.label}` : "Back (ALT + ←)";
  const forwardLabel = canGoForward
    ? `Forward (ALT + →): ${quickNavForwardTarget.label}`
    : "Forward (ALT + →)";
  const inspectParentBodyLabel = jointBodyUuid
    ? `Inspect Parent Body: ${stripPrefix(jointBodyName) || jointBodyUuid}`
    : "Inspect Parent Body";
  const inspectParentObjectLabel = objectLabel
    ? `Inspect Parent Object: ${stripPrefix(objectLabel)}`
    : "Inspect Parent Object";

  const _quickNavActions = [
    {
      key: "back",
      label: backLabel,
      icon: <ArrowLeft className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoBack,
      onDefaultClick: () => {
        if (canGoBack) onQuickNavBack(quickNavBackTarget);
      },
    },
    {
      key: "forward",
      label: forwardLabel,
      icon: <ArrowRight className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoForward,
      onDefaultClick: () => {
        if (canGoForward) onQuickNavForward(quickNavForwardTarget);
      },
    },
    {
      key: "parentBody",
      label: inspectParentBodyLabel,
      icon: <ArrowUp className="w-4 h-4" aria-hidden="true" />,
      disabled: !jointBodyUuid,
      onDefaultClick: () => {
        if (!jointBodyUuid) return;
        selectionActions.setLastSelectionSource("sceneTree");
        selectionActions.selectBody(jointBodyUuid);
        requestInspectorChromeFlash();
      },
    },
    {
      key: "parentObject",
      label: inspectParentObjectLabel,
      icon: <EntireObjectIcon className="w-4 h-4" aria-hidden="true" />,
      disabled: !jointBodyUuid,
      onDefaultClick: () => {
        if (!jointBodyUuid) return;
        handleNavigateToObjectProperties();
      },
    },
  ].filter(Boolean);

  const handleReset = () => {
    try {
      robotActions.updateJointValue(joint.uuid, 0);
      const kind = joint.kind ?? "hinge";
      if (kind === "slide") {
        robotActions.updateJointRange(joint.uuid, 0, 1);
      } else if (kind === "hinge") {
        robotActions.updateJointRange(joint.uuid, -Math.PI, Math.PI);
      }
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to reset joint", error);
    }
  };

  const normalize = (v) => {
    const len = Math.hypot(v[0] || 0, v[1] || 0, v[2] || 0) || 1;
    return [(v[0] || 0) / len, (v[1] || 0) / len, (v[2] || 0) / len];
  };
  const slideAxisForInfo = normalize(axisForInfo);
  const slidePositionOffset =
    Number(posForInfo?.[0] ?? 0) * slideAxisForInfo[0] +
    Number(posForInfo?.[1] ?? 0) * slideAxisForInfo[1] +
    Number(posForInfo?.[2] ?? 0) * slideAxisForInfo[2];
  const slidePositionFromOffset = (offset, axis = slideAxisForInfo) => {
    const n = normalize(axis);
    const scalar = Number(offset);
    const safeScalar = Number.isFinite(scalar) ? scalar : 0;
    return [n[0] * safeScalar, n[1] * safeScalar, n[2] * safeScalar];
  };
  const isAlignedToAxis = (axis, idx) => {
    const unit = [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1],
    ][idx];
    const n = normalize(axis);
    const dot = n[0] * unit[0] + n[1] * unit[1] + n[2] * unit[2];
    return dot > 0 && Math.abs(dot - 1) < SNAP_AXIS_EPSILON;
  };
  const handleAxisSelect = (idx) => {
    const unit = [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1],
    ][idx];
    setAxisValidationMessage("");
    try {
      robotActions.updateJointAxis(joint.uuid, unit);
      if (joint.kind === "slide") {
        robotActions.updateJointPos(joint.uuid, slidePositionFromOffset(slidePositionOffset, unit));
      }
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update joint axis", error);
    }
  };
  const handleAxisChange = (axisIdx, value) => {
    const raw = [
      Number((jointMapEntry?.axis ?? joint?.axis ?? [0, 0, 1])[0] ?? 0),
      Number((jointMapEntry?.axis ?? joint?.axis ?? [0, 0, 1])[1] ?? 0),
      Number((jointMapEntry?.axis ?? joint?.axis ?? [0, 0, 1])[2] ?? 1),
    ];
    raw[axisIdx] = Number(value);
    if (!isValidJointAxis(raw)) {
      setAxisValidationMessage("Rotation axis must have at least one non-zero value.");
      return;
    }
    setAxisValidationMessage("");
    const n = normalize(raw);
    try {
      robotActions.updateJointAxis(joint.uuid, n);
      if (joint.kind === "slide") {
        robotActions.updateJointPos(joint.uuid, slidePositionFromOffset(slidePositionOffset, n));
      }
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update joint axis", error);
    }
  };
  const handleStiffnessChange = (val) => {
    const next = Number(val);
    try {
      robotActions.updateJointStiffness(joint.uuid, Number.isFinite(next) ? next : 0);
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update joint stiffness", error);
    }
  };
  const handleDampingChange = (val) => {
    const next = Number(val);
    try {
      robotActions.updateJointDamping(joint.uuid, Number.isFinite(next) ? next : 0);
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update joint damping", error);
    }
  };
  const handleFrictionLossChange = (val) => {
    const next = Number(val);
    try {
      robotActions.updateJointFrictionLoss(joint.uuid, Number.isFinite(next) ? next : 0);
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update joint friction loss", error);
    }
  };
  const handleKindChange = (nextKind) => {
    try {
      robotActions.updateJointKind(joint.uuid, nextKind);
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update joint kind", error);
    }
  };
  const handleUseLimitsChange = (v) => {
    if (v !== "auto" && v !== "true" && v !== "false") return;
    try {
      robotActions.updateJointUseLimits(joint.uuid, v);
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update joint limit mode", error);
    }
  };
  const handlePosChange = (k, val) => {
    const cur = Array.isArray(posForInfo) ? [...posForInfo] : [0, 0, 0];
    const idx = { x: 0, y: 1, z: 2 }[k];
    cur[idx] = Number(val);
    try {
      robotActions.updateJointPos(joint.uuid, cur);
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update joint position", error);
    }
  };
  const handleSlidePositionOffsetChange = (val) => {
    try {
      robotActions.updateJointPos(joint.uuid, slidePositionFromOffset(val));
    } catch (error) {
      logger.warn("[JointPropertiesPanel] Failed to update slide joint position", error);
    }
  };
  const slidePositionOffsetControl =
    joint.kind === "slide" ? (
      <SlideJointPositionOffsetInput
        disabled={disableJointEditing}
        editingInputs={editingInputs}
        onChange={handleSlidePositionOffsetChange}
        setEditingInputs={setEditingInputs}
        value={slidePositionOffset}
      />
    ) : null;

  const slideAxisControls =
    joint.kind === "slide" ? (
      <JointAxisEditor
        axis={axisForInfo}
        disabled={disableJointEditing}
        editingInputs={editingInputs}
        infoText="The slide axis must be a finite vector with at least one non-zero component."
        isAlignedToAxis={isAlignedToAxis}
        label="Snap to Slide Axis"
        onAxisChange={handleAxisChange}
        onAxisSelect={handleAxisSelect}
        setEditingInputs={setEditingInputs}
        validationMessage={axisValidationMessage}
      />
    ) : null;

  const jointTypeSection = (
    <InspectorPhysicsLockedCollapsibleWrap locked={disableJointEditing}>
      <Collapsible defaultOpen>
        <CollapsibleTrigger quiet>Joint Type</CollapsibleTrigger>
        <CollapsibleContent>
          <fieldset
            disabled={disableJointEditing}
            className={
              disableJointEditing
                ? "pt-2 space-y-4 text-sm text-gray-300 opacity-60 pointer-events-none"
                : "pt-2 space-y-4 text-sm text-gray-300"
            }
          >
            <div>
              <label className="text-xs text-gray-400 select-none inline-flex items-center gap-1.5 mb-1">
                Type
                <InfoTooltip
                  text={
                    "Hinge - single-axis rotation (like a door hinge)\n" +
                    "Slide - linear translation along an axis\n" +
                    "Ball - 3-DOF spherical rotation, no translation\n" +
                    "Free - 6-DOF unconstrained (3 translation + 3 rotation); requires a root body with no other joints\n\n" +
                    "Unavailable types are dimmed when they would create an invalid joint combination on this body."
                  }
                  widthClass="w-72"
                />
              </label>
              <MenuSelect
                value={joint.kind ?? "hinge"}
                onChange={handleKindChange}
                disabled={disableJointEditing}
                options={jointTypeOptions}
                aria-label="Joint type"
              />
            </div>
            {slideAxisControls}
          </fieldset>
        </CollapsibleContent>
      </Collapsible>
    </InspectorPhysicsLockedCollapsibleWrap>
  );

  const jointPhysicsSection = (
    <JointPhysicsSection
      disabled={disableJointEditing}
      editingInputs={editingInputs}
      hasScalarValueRange={hasScalarValueRange}
      joint={joint}
      onDampingChange={handleDampingChange}
      onFrictionLossChange={handleFrictionLossChange}
      onStiffnessChange={handleStiffnessChange}
      onUseLimitsChange={handleUseLimitsChange}
      setEditingInputs={setEditingInputs}
    />
  );
  const ballActuatorSection = (
    <BallJointActuatorsSection
      disabled={disableJointEditing}
      joint={joint}
      jointActuators={jointActuators}
      sceneActions={robotActions}
    />
  );
  if (inspectorMobileScenePeek?.compact) {
    return (
      <div className="p-3 space-y-4">
        <JointVisibilityToolbar jointUuid={joint.uuid} bodyUuid={jointBodyUuid} />
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      <JointVisibilityToolbar jointUuid={joint.uuid} bodyUuid={jointBodyUuid} />

      <Separator />

      {hasRelatedConstraints ? (
        <>
          <RelatedConstraintsSection jointUuid={joint.uuid} defaultTendonsOpen />
          <Separator />
        </>
      ) : null}

      {/* Joint type */}
      {jointTypeSection}
      <Separator />

      {hasScalarValueRange ? (
        <>
          {/* Current joint coordinate + scalar limits (hinge / slide) */}
          <InspectorPhysicsLockedCollapsibleWrap locked={disableJointEditing}>
            <Collapsible defaultOpen>
              <CollapsibleTrigger
                quiet
                aria-label="Toggle value and range"
                actions={
                  <Tooltip
                    content={
                      disableJointEditing
                        ? "Stop physics to reset joint value"
                        : joint.kind === "slide" || joint.kind === "hinge"
                          ? "Reset value to 0 and limits to defaults (slide: 0–1, hinge: ±π)"
                          : "Reset joint value to 0"
                    }
                  >
                    <button
                      type="button"
                      aria-label={
                        joint.kind === "slide" || joint.kind === "hinge"
                          ? "Reset value and limits to defaults"
                          : "Reset joint value"
                      }
                      disabled={disableJointEditing}
                      onClick={(e) => {
                        e.preventDefault();
                        handleReset();
                      }}
                      className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                      style={{ opacity: disableJointEditing ? 0.35 : 0.55 }}
                    >
                      <RotateCcw className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                }
              >
                <span className="min-w-0 truncate text-left">Value & Range</span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <fieldset
                  disabled={disableJointEditing}
                  className={
                    disableJointEditing
                      ? "pt-2 space-y-4 text-sm text-gray-300 opacity-60 pointer-events-none"
                      : "pt-2 space-y-4 text-sm text-gray-300"
                  }
                >
                  <JointScalarTripleRangeSlider
                    jointUuid={joint.uuid}
                    disabled={disableJointEditing}
                  />
                  {slidePositionOffsetControl}
                  {disableJointEditing && (
                    <FieldLabel>
                      Physics is running. Joint editing is disabled; use the sliders in Actuator
                      Controls instead.
                    </FieldLabel>
                  )}
                </fieldset>
              </CollapsibleContent>
            </Collapsible>
          </InspectorPhysicsLockedCollapsibleWrap>

          <Separator />
        </>
      ) : null}

      {hasPivotPosition ? (
        <>
          {/* Position and Rotation Axis */}
          <InspectorPhysicsLockedCollapsibleWrap locked={disableJointEditing}>
            <Collapsible defaultOpen>
              <CollapsibleTrigger
                quiet
                aria-label={
                  hasRotationAxis ? "Toggle Position and Rotation Axis" : "Toggle Pivot Position"
                }
                actions={
                  <Tooltip
                    content={
                      transformEditorBlock.disabled
                        ? transformEditorBlock.title
                        : showPositionGizmo
                          ? "Hide position gizmo"
                          : "Show position gizmo"
                    }
                  >
                    <button
                      type="button"
                      aria-label={showPositionGizmo ? "Hide position gizmo" : "Show position gizmo"}
                      aria-pressed={showPositionGizmo}
                      disabled={transformEditorBlock.disabled}
                      onClick={togglePositionGizmo}
                      className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                      style={{
                        opacity: transformEditorBlock.disabled
                          ? 0.35
                          : showPositionGizmo
                            ? 1
                            : 0.55,
                      }}
                    >
                      <Move3d size={16} />
                    </button>
                  </Tooltip>
                }
              >
                <span className="min-w-0 truncate text-left">
                  {hasRotationAxis ? "Position and Rotation Axis" : "Pivot Position"}
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <fieldset
                  disabled={disableJointEditing}
                  className={
                    disableJointEditing
                      ? "pt-2 space-y-4 text-sm text-gray-300 opacity-60 pointer-events-none"
                      : "pt-2 space-y-4 text-sm text-gray-300"
                  }
                >
                  <div>
                    <FieldLabel className="block mb-2">Pivot Position</FieldLabel>
                    <div className="grid grid-cols-3 gap-2">
                      {["x", "y", "z"].map((k, idx) => {
                        const inputKey = `pos-${idx}`;
                        const isEditing = editingInputs[inputKey];
                        const baseValue = Number(posForInfo?.[idx] ?? 0);
                        const displayValue =
                          isEditing !== undefined ? editingInputs[inputKey] : baseValue;
                        return (
                          <div key={k} className="space-y-1">
                            <AxisNumberInput
                              axis={k}
                              labelClassName="text-xs panel-text-helper uppercase w-3 text-center"
                              value={displayValue}
                              dragValue={baseValue}
                              deferDragUpdates={false}
                              onDragValueChange={(nextValue) => handlePosChange(k, nextValue)}
                              onChange={(e) => {
                                const val = e.target.value;
                                setEditingInputs((prev) => ({ ...prev, [inputKey]: val }));
                                handlePosChange(k, val);
                              }}
                              onFocus={(e) => {
                                setEditingInputs((prev) => ({
                                  ...prev,
                                  [inputKey]: e.target.value,
                                }));
                              }}
                              onBlur={(e) => {
                                const numVal = parseFloat(e.target.value) || 0;
                                handlePosChange(k, numVal);
                                setEditingInputs((prev) => {
                                  const next = { ...prev };
                                  delete next[inputKey];
                                  return next;
                                });
                              }}
                              inputClassName="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm"
                              inputProps={{ step: "0.01", disabled: disableJointEditing }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {hasRotationAxis ? (
                    <JointAxisEditor
                      axis={axisForInfo}
                      disabled={disableJointEditing}
                      editingInputs={editingInputs}
                      infoText="The rotation axis must be a finite vector with at least one non-zero component."
                      isAlignedToAxis={isAlignedToAxis}
                      label="Snap to Rotation Axis"
                      onAxisChange={handleAxisChange}
                      onAxisSelect={handleAxisSelect}
                      setEditingInputs={setEditingInputs}
                      validationMessage={axisValidationMessage}
                    />
                  ) : null}
                </fieldset>
              </CollapsibleContent>
            </Collapsible>
          </InspectorPhysicsLockedCollapsibleWrap>

          <Separator />
        </>
      ) : null}
      {/* Joint physics params */}
      {jointPhysicsSection}

      <Separator />

      {showScalarActuatorsSection ? (
        <ActuatorAccordion
          actuators={jointActuators}
          sceneActions={robotActions}
          addActuatorJoint={
            canAddJointActuator
              ? {
                  ...joint,
                  uuid: joint.uuid,
                  name: joint.name,
                }
              : null
          }
        />
      ) : null}

      {showBallActuatorsSection ? ballActuatorSection : null}
    </div>
  );
};

export default React.memo(JointPropertiesPanel);
